import isEqual from 'lodash.isequal';
import { action, computed, makeObservable, observable } from 'mobx';

import { ToggleStateStore } from 'stores/common';
import { copySet } from 'utils/copySet';

type PrivateFields =
  | '_prevSelectedSet'
  | '_selectedFieldsIdsSet'
  | '_setSelectedSet'
  | '_setPrevSelectedSet';

type FieldId = number;

/**
 * Стор каталога
 */
export class FieldsCatalogControllerStore extends ToggleStateStore {
  constructor() {
    super();

    makeObservable<FieldsCatalogControllerStore, PrivateFields>(this, {
      _selectedFieldsIdsSet: observable,
      _prevSelectedSet: observable.ref,
      selectedFieldsIdsSet: computed,

      choosePreviousSelection: action.bound,

      isDataChanged: computed,

      saveSelectedFieldIds: action.bound,
      _setPrevSelectedSet: action.bound,
      _setSelectedSet: action.bound,
      selectField: action.bound,

      init: action.bound,
    });
  }

  /**
   * Предыдушие выбранные id полей
   * @private
   */
  private _prevSelectedSet: Set<FieldId> = new Set();

  private _selectedFieldsIdsSet: Set<FieldId> = new Set();

  /**
   * выбранные поля
   */
  get selectedFieldsIdsSet(): Set<FieldId> {
    return this._selectedFieldsIdsSet;
  }

  /**
   * Поменялись ли данные?
   */
  get isDataChanged(): boolean {
    return isEqual(
      Array.from(this._prevSelectedSet),
      Array.from(this._selectedFieldsIdsSet)
    );
  }

  /**
   * Функция выбора поля
   * @param fieldId
   * @param isChecked
   */
  selectField(fieldId: FieldId, isChecked: boolean): void {
    if (isChecked) {
      this._selectedFieldsIdsSet.add(fieldId);

      return;
    }

    this._selectedFieldsIdsSet.delete(fieldId);
  }

  private _setPrevSelectedSet(newSet: Set<FieldId>): void {
    this._prevSelectedSet = copySet(newSet);
  }

  /**
   * Выбрано ли поле
   * @param fieldId
   */
  isFieldSelected(fieldId: number): boolean {
    return this._selectedFieldsIdsSet.has(fieldId);
  }

  private _setSelectedSet(newSet: Set<FieldId>): void {
    this._selectedFieldsIdsSet = copySet(newSet);
  }

  /**
   * Инициализация сеттов
   * @param ids
   */
  init(ids: (number | undefined)[]): void {
    const filteredIds = ids.reduce((acc, curr) => {
      if (typeof curr === 'number') {
        return [...acc, curr];
      }

      return acc;
    }, [] as number[]);

    const newSet = new Set(filteredIds);

    this._setPrevSelectedSet(newSet);
    this._setSelectedSet(newSet);
  }

  /**
   * Метод возвращает выбранные id шники и переписывает предыдущие на новые
   */
  saveSelectedFieldIds(): number[] {
    const selectedFieldsIds = Array.from(this._selectedFieldsIdsSet);

    this._setPrevSelectedSet(this._selectedFieldsIdsSet);

    return selectedFieldsIds;
  }

  /**
   * Метод отмены выбора и возврат к предыдщуим выбранным значениям (на кнопку отменить в каталоге)
   */
  choosePreviousSelection() {
    this._selectedFieldsIdsSet = copySet(this._prevSelectedSet);
  }

  cancel(): void {
    this.choosePreviousSelection();
    this.close();
  }

  destroy() {
    super.destroy();

    this._setSelectedSet(new Set());
    this._setPrevSelectedSet(new Set());
  }
}
